/**
 * WorkforceSnapshot — Phase 1B
 *
 * Groups the three employer counters (Open roles / Interest received /
 * Watching) into a single restrained strategic panel.
 *
 * Reuses existing data only. No new API calls. No charts. No colors added
 * beyond the tokens already in use on the prior stat cards.
 */

import { Card } from '@/components/ui/card';
import { Briefcase, FileText, Heart } from 'lucide-react';

function StatCell({ value, label, hint, icon: Icon, iconBg, iconColor, testId }) {
  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div className={`p-2.5 rounded-lg flex-shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
        <p className="text-xs font-medium text-slate-500 mt-0.5">{label}</p>
        {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      </div>
    </div>
  );
}

export function WorkforceSnapshot({
  openRolesCount,
  interestReceivedCount,
  watchingCount,
  pendingInterestCount = 0,
}) {
  return (
    <Card
      className="border border-slate-200 shadow-none mb-8"
      data-testid="employer-workforce-snapshot"
    >
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">
          Workforce snapshot
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 p-5">
        <StatCell
          value={openRolesCount}
          label="Open roles"
          hint="Currently live positions"
          icon={Briefcase}
          iconBg="bg-brand-light"
          iconColor="text-brand"
          testId="stat-active-jobs"
        />
        <StatCell
          value={interestReceivedCount}
          label="Interest received"
          hint={pendingInterestCount > 0 ? `${pendingInterestCount} pending review` : 'Candidates in your pipeline'}
          icon={FileText}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          testId="stat-applications"
        />
        <StatCell
          value={watchingCount}
          label="Watching"
          hint="Profiles bookmarked for review"
          icon={Heart}
          iconBg="bg-violet-50"
          iconColor="text-violet-600"
          testId="stat-saved-candidates"
        />
      </div>
    </Card>
  );
}

export default WorkforceSnapshot;
